import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import adminConsoleService, {
  type BasicField,
  type FieldType,
} from '@services/adminConsoleService';

// ─── Constants ────────────────────────────────────────────────────────────────

// FieldTypeIds that require a list/collection (Person or CustomList)
const LIST_FIELD_TYPE_IDS = [7, 9, 12];
// FieldTypeId 12 is always Person (no selection needed)
const PERSON_FIELD_TYPE_ID = 12;

// ─── Sortable list item for CustomList step ───────────────────────────────────

interface DraftItem {
  id: string; // temp key
  name: string;
  BasicFieldValueId?: number; // present when editing existing field
}

const SortableListItem: React.FC<{
  item: DraftItem;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
}> = ({ item, onDelete, onRename }) =>
{
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const commitEdit = () =>
  {
    const trimmed = draft.trim();
    if (trimmed) onRename(item.id, trimmed);
    else setDraft(item.name);
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 py-1.5 px-2 border border-[#e8e8e8] rounded bg-white mb-1 select-none"
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className="grid grid-cols-2 gap-[2px] cursor-grab active:cursor-grabbing opacity-40 hover:opacity-80 flex-shrink-0 touch-none"
      >
        {[0, 1, 2, 3, 4, 5].map(i => (
          <span key={i} className="w-[3px] h-[3px] rounded-full bg-[#888] block" />
        ))}
      </span>

      {editing ? (
        <input
          autoFocus
          className="flex-1 text-[12px] border border-[#62aacb] rounded px-1.5 py-0.5 outline-none"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={e =>
          {
            if (e.key === 'Enter') commitEdit();
            if (e.key === 'Escape')
            {
              setDraft(item.name);
              setEditing(false);
            }
          }}
        />
      ) : (
        <span
          className="flex-1 text-[12px] text-[#333] cursor-pointer"
          onDoubleClick={() =>
          {
            setDraft(item.name);
            setEditing(true);
          }}
          title="Double-click to rename"
        >
          {item.name}
        </span>
      )}

      <button
        onClick={() => onDelete(item.id)}
        className="flex-shrink-0 text-[#bbb] hover:text-[#e57373] transition-colors"
      >
        <span className="material-icons text-[14px]">close</span>
      </button>
    </div>
  );
};

// ─── Wizard Modal ─────────────────────────────────────────────────────────────

interface WizardProps {
  allFields: BasicField[];
  fieldTypes: FieldType[];
  editTarget: BasicField | null; // null = create mode
  onSave: (field: BasicField) => void;
  onClose: () => void;
}

const FieldWizardModal: React.FC<WizardProps> = ({
  allFields,
  fieldTypes,
  editTarget,
  onSave,
  onClose,
}) =>
{
  const isEdit = editTarget !== null;

  // Step: 0=name/desc, 1=type, 2=listType, 3=listItems
  const [step, setStep] = useState(0);
  const [name, setName] = useState(editTarget?.Name ?? '');
  const [description, setDescription] = useState(editTarget?.Description ?? '');
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(
    editTarget?.FieldTypeId ?? null,
  );
  const [collectionType, setCollectionType] = useState<'Person' | 'CustomListType' | null>(
    editTarget?.CollectionType === 'Person'
      ? 'Person'
      : editTarget?.CollectionType === 'CustomListType'
        ? 'CustomListType'
        : null,
  );
  const [listItems, setListItems] = useState<DraftItem[]>(() =>
    (editTarget?.BasicFieldValues ?? []).map(v => ({
      id: String(v.BasicFieldValueId),
      name: v.Name,
      BasicFieldValueId: v.BasicFieldValueId,
    })),
  );
  const [newItemText, setNewItemText] = useState('');
  const [nameError, setNameError] = useState('');
  const [itemError, setItemError] = useState('');
  const [saving, setSaving] = useState(false);
  const [createAnother, setCreateAnother] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  useEffect(() =>
  {
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }, []);

  const isListType = selectedTypeId !== null && LIST_FIELD_TYPE_IDS.includes(selectedTypeId);
  const isPersonType = selectedTypeId === PERSON_FIELD_TYPE_ID;

  // Total steps depends on type selection
  const totalSteps = (() =>
  {
    if (isEdit) return 1;
    if (!isListType) return 2;
    if (isPersonType) return 2; // auto-selects Person
    return collectionType === 'CustomListType' ? 4 : 3;
  })();

  // ── Validation ────────────────────────────────────────────────────────────

  const validateName = useCallback(
    (value: string): string =>
    {
      if (!value.trim()) return 'Name is required';
      const dup = allFields.find(
        f =>
          f.Name.toLowerCase() === value.trim().toLowerCase() &&
          (!isEdit || f.BasicFieldId !== editTarget!.BasicFieldId),
      );
      if (dup) return 'The field name already exists';
      return '';
    },
    [allFields, isEdit, editTarget],
  );

  // ── List item helpers ─────────────────────────────────────────────────────

  const addListItem = () =>
  {
    const trimmed = newItemText.trim();
    if (!trimmed)
    {
      setItemError('Please enter a value');
      return;
    }
    if (listItems.some(i => i.name.toLowerCase() === trimmed.toLowerCase()))
    {
      setItemError('Item already added');
      return;
    }
    setListItems(prev => [...prev, { id: `new-${Date.now()}`, name: trimmed }]);
    setNewItemText('');
    setItemError('');
  };

  const deleteListItem = (id: string) => setListItems(prev => prev.filter(i => i.id !== id));

  const renameListItem = (id: string, name: string) =>
    setListItems(prev => prev.map(i => (i.id === id ? { ...i, name } : i)));

  const handleListDragEnd = (event: DragEndEvent) =>
  {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = listItems.findIndex(i => i.id === active.id);
    const newIdx = listItems.findIndex(i => i.id === over.id);
    setListItems(arrayMove(listItems, oldIdx, newIdx));
  };

  // ── Navigation ────────────────────────────────────────────────────────────

  const canGoNext = (): boolean =>
  {
    if (step === 0)
      return name.trim().length > 0 && description.trim().length > 0 && !validateName(name);
    if (step === 1) return selectedTypeId !== null;
    if (step === 2) return collectionType !== null;
    return true;
  };

  const goNext = () =>
  {
    if (step === 0)
    {
      const err = validateName(name);
      if (err || !description.trim())
      {
        setNameError(err || 'Name and description are required');
        return;
      }
    }
    setStep(s => s + 1);
  };

  // ── Save ──────────────────────────────────────────────────────────────────

  const buildPayload = () =>
  {
    const ordered = listItems.map((item, idx) => ({ ...item, tempOrder: idx }));

    if (isEdit)
    {
      const isCustomList = editTarget!.CollectionType === 'CustomListType';
      return {
        update: {
          BasicFieldId: editTarget!.BasicFieldId,
          Name: name.trim(),
          Description: description.trim(),
          BasicFieldValues: isCustomList
            ? ordered.map((i, idx) => ({
              BasicFieldValueId: i.BasicFieldValueId,
              Name: i.name,
              tempOrder: idx,
            }))
            : null,
        },
      };
    }

    // Create
    let serviceUrl: string | null = null;
    let textField: string | null = null;
    let valueField: string | null = null;
    let collection: string | null = null;
    let values: { Name: string; tempOrder: number }[] | null = null;

    if (isPersonType || collectionType === 'Person')
    {
      serviceUrl = 'api/person/getpeoplebyconfigvalue';
      textField = 'Name';
      valueField = 'PersonID';
      collection = 'Person';
    }
    else if (collectionType === 'CustomListType')
    {
      serviceUrl = null; // set by backend after creation
      textField = 'Name';
      valueField = 'BasicFieldValueId';
      collection = 'CustomListType';
      values = ordered.map((i, idx) => ({ Name: i.name, tempOrder: idx }));
    }

    return {
      create: {
        Name: name.trim(),
        Description: description.trim(),
        FieldTypeId: selectedTypeId!,
        ServiceUrl: serviceUrl,
        CollectionType: collection,
        ValueField: valueField,
        TextField: textField,
        BasicFieldValues: values,
      },
    };
  };

  const handleSave = async () =>
  {
    const err = validateName(name);
    if (err)
    {
      setNameError(err);
      return;
    }
    if (!description.trim())
    {
      setNameError('Name and description are required');
      return;
    }

    setSaving(true);
    try
    {
      const payload = buildPayload();

      if (payload.update)
      {
        const updatedValues = await adminConsoleService.updateBasicField(payload.update);
        const updatedField: BasicField = {
          ...editTarget!,
          Name: payload.update.Name,
          Description: payload.update.Description,
          BasicFieldValues:
            updatedValues.length > 0
              ? updatedValues
              : listItems.map((i, idx) => ({
                BasicFieldValueId: i.BasicFieldValueId ?? 0,
                BasicFieldId: editTarget!.BasicFieldId,
                Name: i.name,
                order: idx,
                tempOrder: idx,
              })),
        };
        onSave(updatedField);
      }
      else if (payload.create)
      {
        const newId = await adminConsoleService.addBasicField(payload.create);
        const newField: BasicField = {
          BasicFieldId: newId,
          FieldTypeId: payload.create.FieldTypeId,
          Name: payload.create.Name,
          Description: payload.create.Description,
          ServiceUrl: payload.create.ServiceUrl,
          TextField: payload.create.TextField,
          ValueField: payload.create.ValueField,
          IsUsedInLayer: null,
          CollectionType: payload.create.CollectionType,
          IsDeleted: false,
          BasicFieldValues: (payload.create.BasicFieldValues ?? []).map((v, idx) => ({
            BasicFieldValueId: 0,
            BasicFieldId: newId,
            Name: v.Name,
            order: idx,
            tempOrder: idx,
          })),
        };
        onSave(newField);
        if (createAnother)
        {
          // Reset for new entry
          setName('');
          setDescription('');
          setSelectedTypeId(null);
          setCollectionType(null);
          setListItems([]);
          setStep(0);
          setNameError('');
          setSaving(false);
          return;
        }
      }
      onClose();
    }
    catch
    {
      setNameError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  // ── Show save button ──────────────────────────────────────────────────────

  const showSave =
    isEdit ||
    (step === 1 && selectedTypeId !== null && !isListType) ||
    (step === 1 && isPersonType) ||
    (step === 2 && collectionType === 'Person') ||
    step === 3;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[520px] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0] flex-shrink-0">
          <h2 className="text-[14px] font-semibold text-[#333]">
            {isEdit ? 'Edit Custom Detail' : 'New Custom Detail'}
          </h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Step 0: Name + Description */}
          {step === 0 && (
            <div className="flex flex-col gap-4">
              <p className="text-[11px] text-[#888] uppercase tracking-wider font-medium">
                Custom Details Description
              </p>
              <div>
                <label className="block text-[12px] text-[#555] mb-1">Custom detail name</label>
                <input
                  ref={nameInputRef}
                  type="text"
                  value={name}
                  onChange={e =>
                  {
                    setName(e.target.value);
                    setNameError('');
                  }}
                  className="w-full border border-[#ddd] rounded px-3 py-1.5 text-[13px] outline-none focus:border-[#62aacb]"
                  placeholder="e.g. Priority"
                />
              </div>
              <div>
                <label className="block text-[12px] text-[#555] mb-1">
                  Custom detail description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={e =>
                  {
                    setDescription(e.target.value);
                    setNameError('');
                  }}
                  className="w-full border border-[#ddd] rounded px-3 py-1.5 text-[13px] outline-none focus:border-[#62aacb]"
                  placeholder="e.g. Card priority level"
                  onKeyDown={e =>
                  {
                    if (e.key === 'Enter' && !isEdit) goNext();
                    if (e.key === 'Enter' && isEdit) handleSave();
                  }}
                />
              </div>

              {/* List items section in edit mode */}
              {isEdit && editTarget?.CollectionType === 'CustomListType' && (
                <div>
                  <p className="text-[12px] text-[#555] mb-2 font-medium">List values</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newItemText}
                      onChange={e =>
                      {
                        setNewItemText(e.target.value);
                        setItemError('');
                      }}
                      onKeyDown={e =>
                      {
                        if (e.key === 'Enter') addListItem();
                      }}
                      className="flex-1 border border-[#ddd] rounded px-2.5 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
                      placeholder="Add a value…"
                    />
                    <button
                      onClick={addListItem}
                      className="px-3 py-1.5 bg-[#62aacb] text-white rounded text-[12px] hover:bg-[#4e99ba] transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {itemError && <p className="text-[11px] text-[#e57373] mb-1">{itemError}</p>}
                  <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleListDragEnd}
                  >
                    <SortableContext
                      items={listItems.map(i => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="max-h-[200px] overflow-y-auto">
                        {listItems.map(item => (
                          <SortableListItem
                            key={item.id}
                            item={item}
                            onDelete={deleteListItem}
                            onRename={renameListItem}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {nameError && <p className="text-[12px] text-[#e57373]">{nameError}</p>}
            </div>
          )}

          {/* Step 1: Select field type */}
          {step === 1 && !isEdit && (
            <div>
              <p className="text-[11px] text-[#888] uppercase tracking-wider font-medium mb-3">
                Select a Details Type
              </p>
              <div className="grid grid-cols-3 gap-2">
                {fieldTypes.map(ft => (
                  <button
                    key={ft.FieldTypeId}
                    onClick={() => setSelectedTypeId(ft.FieldTypeId)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded border text-[12px] transition-colors text-left ${
                      selectedTypeId === ft.FieldTypeId
                        ? 'border-[#62aacb] bg-[#e8f4f9] text-[#62aacb]'
                        : 'border-[#e0e0e0] bg-white text-[#444] hover:border-[#62aacb]'
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                        selectedTypeId === ft.FieldTypeId
                          ? 'border-[#62aacb] bg-[#62aacb]'
                          : 'border-[#ccc]'
                      }`}
                    />
                    {ft.Description || ft.Name}
                    {(ft.FieldTypeId == 1 ||
                      ft.FieldTypeId == 5 ||
                      ft.FieldTypeId == 10 ||
                      ft.FieldTypeId == 11) && (
                      <i
                        className="material-icons not-filterable enable-help-tooltip"
                        title="This detail type is not filterable"
                      >
                        do_not_disturb_on
                      </i>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select list type (Person vs CustomList) */}
          {step === 2 && !isEdit && !isPersonType && (
            <div>
              <p className="text-[11px] text-[#888] uppercase tracking-wider font-medium mb-3">
                Select a List Type
              </p>
              <div className="flex flex-col gap-3">
                {[
                  {
                    value: 'Person' as const,
                    label: 'Person',
                    desc: 'Use the people/users collection',
                  },
                  {
                    value: 'CustomListType' as const,
                    label: 'Custom List',
                    desc: 'Define your own list of values',
                  },
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setCollectionType(opt.value)}
                    className={`flex items-start gap-3 px-4 py-3 rounded border text-left transition-colors ${
                      collectionType === opt.value
                        ? 'border-[#62aacb] bg-[#e8f4f9]'
                        : 'border-[#e0e0e0] bg-white hover:border-[#62aacb]'
                    }`}
                  >
                    <span
                      className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                        collectionType === opt.value
                          ? 'border-[#62aacb] bg-[#62aacb]'
                          : 'border-[#ccc]'
                      }`}
                    />
                    <div>
                      <p className="text-[13px] font-medium text-[#333]">{opt.label}</p>
                      <p className="text-[11px] text-[#888]">{opt.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Configure custom list items */}
          {step === 3 && !isEdit && collectionType === 'CustomListType' && (
            <div>
              <p className="text-[11px] text-[#888] uppercase tracking-wider font-medium mb-3">
                Configure List Values
              </p>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newItemText}
                  onChange={e =>
                  {
                    setNewItemText(e.target.value);
                    setItemError('');
                  }}
                  onKeyDown={e =>
                  {
                    if (e.key === 'Enter') addListItem();
                  }}
                  className="flex-1 border border-[#ddd] rounded px-2.5 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
                  placeholder="Add a value…"
                  autoFocus
                />
                <button
                  onClick={addListItem}
                  className="px-3 py-1.5 bg-[#62aacb] text-white rounded text-[12px] hover:bg-[#4e99ba] transition-colors"
                >
                  Add
                </button>
              </div>
              {itemError && <p className="text-[11px] text-[#e57373] mb-1">{itemError}</p>}
              {listItems.length === 0 && (
                <p className="text-[11px] text-[#aaa] italic mb-2">
                  No values yet. Add at least one.
                </p>
              )}
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleListDragEnd}
              >
                <SortableContext
                  items={listItems.map(i => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="max-h-[240px] overflow-y-auto">
                    {listItems.map(item => (
                      <SortableListItem
                        key={item.id}
                        item={item}
                        onDelete={deleteListItem}
                        onRename={renameListItem}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-[#e0e0e0] px-5 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {/* Back / Cancel */}
            {step > 0 && !isEdit ? (
              <button
                onClick={() => setStep(s => s - 1)}
                className="px-4 py-1.5 text-[12px] border border-[#ddd] rounded text-[#555] hover:bg-[#f5f5f5] transition-colors"
              >
                Back
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-1.5 text-[12px] border border-[#ddd] rounded text-[#555] hover:bg-[#f5f5f5] transition-colors"
              >
                Cancel
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Step counter */}
            {!isEdit && totalSteps > 1 && (
              <span className="text-[11px] text-[#aaa]">
                {step + 1}/{totalSteps}
              </span>
            )}

            {/* Create another (only in create mode, when about to save) */}
            {!isEdit && showSave && (
              <label className="flex items-center gap-1.5 text-[11px] text-[#666] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={createAnother}
                  onChange={e => setCreateAnother(e.target.checked)}
                  className="accent-[#62aacb]"
                />
                Create another
              </label>
            )}

            {/* Next or Save */}
            {showSave ? (
              <button
                onClick={handleSave}
                disabled={saving || !canGoNext()}
                className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            ) : (
              <button
                onClick={goNext}
                disabled={!canGoNext()}
                className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
              >
                Next &rsaquo;
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

interface CustomDetailsTabProps {
  prevOverflow: boolean;
}

export const CustomDetailsTab: React.FC<CustomDetailsTabProps> = ({ prevOverflow = false }) =>
{
  const [fields, setFields] = useState<BasicField[]>([]);
  const [fieldTypes, setFieldTypes] = useState<FieldType[]>([]);
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [inUseError, setInUseError] = useState(false);
  const [filter, setFilter] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [editTarget, setEditTarget] = useState<BasicField | null>(null);
  const inUseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() =>
  {
    if (loading) return;
    setLoading(true);
    Promise.all([adminConsoleService.getBasicFields(), adminConsoleService.getFieldTypes()])
      .then(([f, ft]) =>
      {
        setFields(f.filter(x => !x.IsDeleted));
        setFieldTypes(ft);
      })
      .catch(() => setError('Failed to load custom details.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = fields
    .filter(f => f.Name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) =>
      sortDir === 'asc'
        ? a.Name.localeCompare(b.Name)
        : sortDir === 'desc'
          ? b.Name.localeCompare(a.Name)
          : 0,
    );

  const openCreate = () =>
  {
    setEditTarget(null);
    setShowWizard(true);
  };
  const openEdit = (f: BasicField) =>
  {
    setEditTarget(f);
    setShowWizard(true);
  };

  const handleSave = useCallback((saved: BasicField) =>
  {
    setFields(prev =>
    {
      const exists = prev.find(f => f.BasicFieldId === saved.BasicFieldId);
      return exists
        ? prev.map(f => (f.BasicFieldId === saved.BasicFieldId ? saved : f))
        : [...prev, saved];
    });
  }, []);

  const handleDelete = useCallback(async (field: BasicField) =>
  {
    try
    {
      const ok = await adminConsoleService.deleteBasicField(field.BasicFieldId);
      if (ok)
      {
        setFields(prev => prev.filter(f => f.BasicFieldId !== field.BasicFieldId));
      }
      else
      {
        if (inUseTimerRef.current) clearTimeout(inUseTimerRef.current);
        setInUseError(true);
        inUseTimerRef.current = setTimeout(() => setInUseError(false), 4000);
      }
    }
    catch
    {
      setError('Failed to delete field.');
    }
  }, []);

  const fieldTypeName = (id: number) =>
    fieldTypes.find(ft => ft.FieldTypeId === id)?.Description ||
    fieldTypes.find(ft => ft.FieldTypeId === id)?.Name ||
    String(id);

  return (
    <div className="flex flex-col h-full">
      {/* ── Toolbar ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 border-b border-[#e8e8e8] bg-white gap-3">
        <div className="relative">
          <span className="material-icons absolute left-2.5 top-1/2 -translate-y-1/2 text-[#bbb] text-[16px]">
            search
          </span>
          <input
            type="text"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Find by keyword"
            className="pl-8 pr-3 py-1.5 border border-[#ddd] rounded text-[12px] outline-none focus:border-[#62aacb] w-[220px]"
          />
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 text-[#62aacb] text-[12px] hover:text-[#4e99ba] transition-colors"
        >
          <span className="material-icons text-[18px] text-[#beb7ac]">add_circle_outline</span>
          <span className="hover:underline">Add Custom Details</span>
        </button>
      </div>

      {/* ── Errors ── */}
      {/* {error && (
        <div className="mx-6 mt-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)}><span className="material-icons text-[14px]">close</span></button>
        </div>
      )} */}
      {inUseError && (
        <div className="mx-6 mt-3 px-3 py-2 bg-[#fff3e0] border border-[#ffb74d] rounded text-[12px] text-[#e65100] flex items-center gap-2 flex-shrink-0">
          <span className="material-icons text-[14px]">warning_amber</span>
          The field is being used and cannot be deleted.
        </div>
      )}

      {/* ── Table ── */}
      <div
        className={`flex-1 overflow-y-auto px-6 py-4 ${prevOverflow ? 'overflow-x-hidden' : ''}`}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#aaa] text-sm gap-2">
            <span className="material-icons text-[32px]">tune</span>
            {filter ? 'No results match your search.' : 'No custom details yet.'}
          </div>
        ) : (
          <table className="w-full border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-[#e0e0e0] text-[#888]">
                <th className="text-left py-2 px-3 font-medium w-[40%]">
                  <button
                    onClick={() =>
                      setSortDir(d => (d === null ? 'asc' : d === 'asc' ? 'desc' : null))
                    }
                    className="flex items-center gap-1 hover:text-[#333] transition-colors"
                  >
                    Name
                    {sortDir !== null && (
                      <span className="material-icons text-[13px]">
                        {sortDir === 'desc' ? 'arrow_downward' : 'arrow_upward'}
                      </span>
                    )}
                  </button>
                </th>
                <th className="text-left py-2 px-3 font-medium w-[25%]">Description</th>
                <th className="text-left py-2 px-3 font-medium w-[20%]">Type</th>
                <th className="py-2 px-3 w-[15%]" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(field => (
                <tr
                  key={field.BasicFieldId}
                  className="border-b border-[#f0f0f0] hover:bg-[#fafafa] transition-colors"
                >
                  <td className="py-2 px-3 font-medium text-[#333]">{field.Name}</td>
                  <td className="py-2 px-3 text-[#666]">{field.Description}</td>
                  <td className="py-2 px-3 text-[#888]">{fieldTypeName(field.FieldTypeId)}</td>
                  <td className="py-2 px-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(field)}
                        className="text-[#bbb] hover:text-[#62aacb] transition-colors w-8"
                        title="Edit"
                      >
                        <span className="material-icons text-[16px] dark:text-[#b9b1a6]">edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(field)}
                        className="text-[#bbb] hover:text-[#e57373] transition-colors w-8"
                        title="Delete"
                      >
                        <span className="material-icons text-[16px] dark:text-[#b9b1a6]">
                          delete_outline
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Wizard Modal ── */}
      {showWizard && (
        <FieldWizardModal
          allFields={fields}
          fieldTypes={fieldTypes}
          editTarget={editTarget}
          onSave={handleSave}
          onClose={() => setShowWizard(false)}
        />
      )}
    </div>
  );
};
